/**
 * Газрын зураг — Leaflet + OpenStreetMap.
 *
 * НЭГ компонент, ХОЁР хэрэглээ:
 *   · Маягт дотор цэг СОНГОХ (`onPick` өгнө, `points` нь 0–1 цэг)
 *   · Жагсаалтын дээр бүх салбарыг ХАРУУЛАХ (`points` олон, `onPick` үгүй)
 * Хоёрыг тусад нь бичвэл tile, икон, цэвэрлэгээ гурав давхардана.
 *
 * ⚠️ ГУРАВДАГЧ ДОМЭЙН. Tile нь `tile.openstreetmap.org`-оос татагдана.
 *    CLAUDE.md-ийн «Оросоос дуудагдах гуравдагч домэйн тэг» дүрэм нь
 *    ОРОСЫН ОФЛАЙН аппад (`apps/mobile`) хамаарна. Энэ нь Улаанбаатарын
 *    админ панель, интернэттэй ажиллана — захиалагч 2026-09-03-нд
 *    зөвшөөрсөн.
 *
 *    Tile ирээгүй ч бүртгэл ажиллана: координатыг гараар оруулах
 *    талбар `StoreForm`-д бий, зөвхөн дэвсгэр зураг хоосон харагдана.
 *
 * ⚠️ `react-leaflet` НЭМЭЭГҮЙ — нэмэлт dependency, ердөө хоёр газарт
 *    хэрэглэгдэх зүйлд илүүц (CLAUDE.md — шинэ хамаарал нэмэхээс өмнө
 *    асуу). Leaflet-ийн API энгийн, `useEffect` хангалттай.
 *
 * ⚠️ Leaflet-ийн АНХДАГЧ marker икон ашиглаагүй: тэр нь bundler дор
 *    эвдэрдэг PNG зам (`marker-icon.png`) шаарддаг. Оронд нь `divIcon`
 *    — цэвэр HTML, брэндийн өнгөтэй, нэмэлт файлгүй.
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapPoint {
  id: string;
  /** Тэмдэг дээр БИЧИГДЭХ богино нэр — салбарын код («S1», «WH») */
  code: string;
  /** Хулганаар дээр очиход гарах бүтэн нэр */
  label: string;
  lat: number;
  lng: number;
}

/** Улаанбаатарын төв — цэг байхгүй үед энд төвлөрнө. */
const UB: [number, number] = [47.9186, 106.9177];
const CITY_ZOOM = 12;
const POINT_ZOOM = 16;

/** HTML-д шууд оруулахын өмнө — код нь хэрэглэгчийн оруулсан утга. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/**
 * Тэмдэг дээр САЛБАРЫН КОД бичигдэнэ.
 *
 * Өмнө нь бүх цэг ижил өнгийн дугуй байсан тул «аль нь S1, аль нь S2»
 * гэдгийг зөвхөн хулгана дээр аваачиж мэдэх боломжтой байв — 3-4 салбар
 * ойрхон байхад бүрмөсөн ялгагдахгүй. Одоо код нь зураг дээрээ.
 *
 * Өнгө нь `--color-accent` (#0f7d77). CSS хувьсагч Leaflet-ийн HTML
 * мөрөнд хүрэхгүй тул шууд бичив.
 */
function pinFor(code: string): L.DivIcon {
  const text = escapeHtml(code);
  return L.divIcon({
    className: '',
    html:
      '<span style="display:inline-flex;align-items:center;height:22px;padding:0 8px;'
      + 'border-radius:999px;background:#0f7d77;border:2px solid #fff;color:#fff;'
      + 'font:600 11px/1 system-ui,sans-serif;letter-spacing:.02em;white-space:nowrap;'
      + `box-shadow:0 1px 4px rgba(0,0,0,.4)">${text}</span>`,
    // Өргөн нь кодын уртаас хамаарна — Leaflet-д яг утга хэрэгтэй тул
    // тэмдэгтийн тоогоор ойролцоолж, төвийг нь доод талд тогтооно.
    iconSize: [code.length * 7 + 20, 22],
    iconAnchor: [(code.length * 7 + 20) / 2, 11],
  });
}

export function MapView({
  points, onPick, height = 'h-56', hint,
}: {
  points: MapPoint[];
  /** Өгвөл зураг дээр дарж координат сонгоно. */
  onPick?: (lat: number, lng: number) => void;
  /** Tailwind өндөр — `h-56` (маягт) / `h-72` (жагсаалт). */
  height?: string;
  /** Зургийн доорх тайлбар. `onPick`-гүй үед ихэвчлэн хэрэггүй. */
  hint?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  /**
   * `onPick` нь рендер бүрд шинэ функц болно. Түүнийг `useEffect`-ийн
   * хамаарал болговол зураг рендер бүрд дахин үүсэж анивчина —
   * тиймээс ref-ээр хамгийн сүүлийн хувилбарыг барина.
   */
  const pick = useRef(onPick);
  pick.current = onPick;

  // Зургийг НЭГ л удаа үүсгэнэ
  useEffect(() => {
    if (!host.current || map.current) return;

    const m = L.map(host.current, {
      center: UB,
      zoom: CITY_ZOOM,
      // Хуудас гүйлгэхэд зураг санамсаргүй томордог — товчоор нь л
      scrollWheelZoom: false,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      // ODbL лицензийн шаардлага — устгаж болохгүй
      attribution: '© OpenStreetMap',
    }).addTo(m);

    m.on('click', (e: L.LeafletMouseEvent) => {
      pick.current?.(
        Number(e.latlng.lat.toFixed(6)),
        Number(e.latlng.lng.toFixed(6)),
      );
    });

    layer.current = L.layerGroup().addTo(m);
    map.current = m;

    /**
     * ⚠️ МОДАЛ ДОТОР ЗААВАЛ. Leaflet нь үүсэх мөчид контейнерийн
     *    хэмжээг уншиж tile-аа тооцдог. Модал нээгдэх үед layout
     *    хараахан суугаагүй байвал өндөр 0 болж, зураг САРААЛ хоосон
     *    үлддэг. `invalidateSize` нь дараагийн frame-д дахин хэмжинэ.
     */
    const settle = requestAnimationFrame(() => m.invalidateSize());

    return () => {
      cancelAnimationFrame(settle);
      m.remove();
      map.current = null;
      layer.current = null;
    };
    // Хамааралгүй — зураг НЭГ л удаа үүсэж, цэгүүд доорх effect-ээр
    // шинэчлэгдэнэ. Дахин үүсгэвэл зураг анивчиж, гүйлгэсэн байрлал
    // тэглэгдэнэ.
  }, []);

  // Цэгүүд солигдоход давхаргыг бүхэлд нь дахин зурна
  useEffect(() => {
    const m = map.current;
    const g = layer.current;
    if (!m || !g) return;

    g.clearLayers();
    for (const p of points) {
      L.marker([p.lat, p.lng], { icon: pinFor(p.code), title: p.label })
        .bindTooltip(p.label, { direction: 'top', offset: [0, -14] })
        .addTo(g);
    }

    const only = points.length === 1 ? points[0] : undefined;
    if (only) {
      m.setView([only.lat, only.lng], Math.max(m.getZoom(), POINT_ZOOM));
    } else if (points.length > 1) {
      // Бүх цэг багтах хүрээ. `padding` — тэмдэг зах дээр наалдахгүй.
      m.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), {
        padding: [28, 28],
        maxZoom: POINT_ZOOM,
      });
    }
  }, [points]);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        ref={host}
        className={`${height} w-full overflow-hidden rounded-well border border-hair ${
          onPick ? 'cursor-crosshair' : ''
        }`}
        role="application"
        aria-label="Байршлын газрын зураг"
      />
      {hint ? <p className="text-[11px] text-ink-3">{hint}</p> : null}
    </div>
  );
}
